import { IAuthorization } from '@studyportals/bodies-editor-integration';
import {
	BaseSuperAgentRequestFactory,
	IRequestSender,
	ISuperAgentRequestFactory,
	RequestSender,
} from '@studyportals/mb-platform-http-requests';

import { ReflectorResponse } from './ReflectorResponse';
import { ResponseError } from 'superagent';
import { ReflectorDataNotFoundError } from './ReflectorDataNotFoundError';
import { ReflectorRequestError } from './ReflectorRequestError';
import { ReflectorInternalError } from './ReflectorInternalError';
import { RequestSenderDecoratorUiLoader } from '@/infrastructure/ui-loader/RequestSenderDecoratorUiLoader';
import { RequestSenderDecoratorReflectorRetry } from '@/infrastructure/reflector/RequestSenderDecoratorReflectorRetry';

export class ReflectorIntegration {
	private readonly requestSender: IRequestSender;
	private readonly requestFactory: ISuperAgentRequestFactory;

	public constructor() {
		this.requestSender = new RequestSender();
		this.requestSender = new RequestSenderDecoratorReflectorRetry(
			this.requestSender,
		);
		this.requestSender = new RequestSenderDecoratorUiLoader(this.requestSender);
		this.requestFactory = new BaseSuperAgentRequestFactory();
	}

	protected async sendGet<TData>(
		path: string,
		q: string | null,
		authorization: IAuthorization,
		offset?: number,
		length = 25,
	): Promise<readonly TData[]> {
		let uri = `${CONFIG.REFLECTOR_URI}?token=${authorization.token}&path=${path}&length=${length}`;

		if (q !== null) {
			uri += `&q=${q}`;
		}

		if (offset !== undefined) {
			uri += `&start=${offset}`;
		}

		const request = this.requestFactory.get(uri);
		void request.set('Cache-Control', 'no-cache');

		const response =
			await this.requestSender.sendAndExtractBody<ReflectorResponse<TData>>(
				request,
			);
		return this.extractObjects(response);
	}

	protected async sendPut<TData>(
		path: string,
		bodyFromUrlEncoded: string,
		authorization: IAuthorization,
	): Promise<readonly TData[]> {
		const uri = CONFIG.REFLECTOR_URI;
		const request = this.requestFactory
			.put(uri)
			.set('Content-Type', 'application/x-www-form-urlencoded')
			.send(`${bodyFromUrlEncoded}&token=${authorization.token}&path=${path}`);

		const response =
			await this.requestSender.sendAndExtractBody<ReflectorResponse<TData>>(
				request,
			);
		return this.extractObjects(response);
	}

	protected async sendPost<TData>(
		path: string,
		bodyFromUrlEncoded: string,
		authorization: IAuthorization,
	): Promise<readonly TData[]> {
		const uri = CONFIG.REFLECTOR_URI;
		const request = this.requestFactory
			.post(uri)
			.set('Content-Type', 'application/x-www-form-urlencoded')
			.send(`${bodyFromUrlEncoded}&token=${authorization.token}&path=${path}`);

		const response =
			await this.requestSender.sendAndExtractBody<ReflectorResponse<TData>>(
				request,
			);
		return this.extractObjects(response);
	}

	private extractObjects<TData>(
		response: ReflectorResponse<TData>,
	): readonly TData[] {
		return Array.isArray(response) ? [] : Object.values(response);
	}

	protected tryThrowAbstractedError(error: ResponseError): void {
		if (error.status === undefined) {
			return;
		}

		if (error.status === 404) {
			throw new ReflectorDataNotFoundError();
		}

		if (error.status >= 400 && error.status < 500) {
			throw new ReflectorRequestError();
		}

		if (error.status >= 500 && error.status < 600) {
			throw new ReflectorInternalError();
		}
	}
}
